import React, { useEffect, useId, useState } from "react";
import { createPortal } from "react-dom";
import { createFeatureIdea, FeatureIdeaSummary } from "../services/featureRequestsService";
import "../styles/feature-requests.css";

const TITLE_MAX = 250;
const DESCRIPTION_MAX = 1000;

type Props = {
    open: boolean;
    onClose: () => void;
    onSubmitted: (idea: FeatureIdeaSummary) => void;
};

export default function SubmitFeatureIdeaModal({ open, onClose, onSubmitted }: Props) {
    const titleId = useId();
    const descriptionId = useId();
    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [titleError, setTitleError] = useState<string | null>(null);
    const [descriptionError, setDescriptionError] = useState<string | null>(null);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [touched, setTouched] = useState({ title: false, description: false });

    useEffect(() => {
        if (!open) return;
        setTitle("");
        setDescription("");
        setTitleError(null);
        setDescriptionError(null);
        setSubmitError(null);
        setSubmitting(false);
        setTouched({ title: false, description: false });
    }, [open]);

    useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") onClose();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open, onClose]);

    const validateTitle = (value: string): string | null => {
        const trimmed = value.trim();
        if (!trimmed) return "Please enter a discussion title.";
        if (trimmed.length > TITLE_MAX) return "Discussion title must be 250 characters or fewer.";
        return null;
    };

    const validateDescription = (value: string): string | null => {
        const trimmed = value.trim();
        if (!trimmed) return "Please enter a description.";
        if (trimmed.length > DESCRIPTION_MAX) return "Description must be 1,000 characters or fewer.";
        return null;
    };

    const titleErr = validateTitle(title);
    const descriptionErr = validateDescription(description);
    const isValid = !titleErr && !descriptionErr;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setTouched({ title: true, description: true });
        setTitleError(validateTitle(title));
        setDescriptionError(validateDescription(description));
        if (!isValid) return;

        setSubmitting(true);
        setSubmitError(null);
        try {
            const created = await createFeatureIdea(title.trim(), description.trim());
            onSubmitted(created);
            onClose();
        } catch (err: unknown) {
            setSubmitError(err instanceof Error ? err.message : "Failed to create post.");
        } finally {
            setSubmitting(false);
        }
    };

    if (!open) return null;

    return createPortal(
        <div
            className="feature-idea-modal-backdrop"
            onClick={(e) => {
                if (e.target === e.currentTarget) onClose();
            }}
        >
            <div
                className="feature-idea-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby={`${titleId}-heading`}
                data-testid="submit-feature-idea-modal"
            >
                <div className="feature-idea-modal-header">
                    <button
                        type="button"
                        className="feature-idea-modal-close"
                        onClick={onClose}
                        aria-label="Close"
                    >
                        ×
                    </button>
                    <h2 id={`${titleId}-heading`}>Create a Post</h2>
                    <p>
                        Share a question, idea, or discussion with the Polaris Athlete community.
                    </p>
                </div>

                <form className="feature-idea-modal-body" onSubmit={handleSubmit} noValidate>
                    <div className="feature-idea-field">
                        <label htmlFor={titleId}>What would you like to discuss?</label>
                        <input
                            id={titleId}
                            type="text"
                            value={title}
                            maxLength={TITLE_MAX}
                            data-testid="feature-idea-title-input"
                            onChange={(e) => {
                                setTitle(e.target.value);
                                if (touched.title) setTitleError(validateTitle(e.target.value));
                            }}
                            onBlur={() => {
                                setTouched((t) => ({ ...t, title: true }));
                                setTitleError(validateTitle(title));
                            }}
                        />
                        <div className="feature-idea-field-meta">
                            {(touched.title && titleError) || titleError ? (
                                <span className="feature-idea-field-error" role="alert">
                                    {titleError || (touched.title ? titleErr : null)}
                                </span>
                            ) : (
                                <span />
                            )}
                            <span>{title.length}/{TITLE_MAX}</span>
                        </div>
                    </div>

                    <div className="feature-idea-field">
                        <label htmlFor={descriptionId}>
                            Description (Share more details with the community...)
                        </label>
                        <textarea
                            id={descriptionId}
                            value={description}
                            maxLength={DESCRIPTION_MAX}
                            data-testid="feature-idea-description-input"
                            onChange={(e) => {
                                setDescription(e.target.value);
                                if (touched.description) setDescriptionError(validateDescription(e.target.value));
                            }}
                            onBlur={() => {
                                setTouched((t) => ({ ...t, description: true }));
                                setDescriptionError(validateDescription(description));
                            }}
                        />
                        <div className="feature-idea-field-meta">
                            {(touched.description && descriptionError) || descriptionError ? (
                                <span className="feature-idea-field-error" role="alert">
                                    {descriptionError || (touched.description ? descriptionErr : null)}
                                </span>
                            ) : (
                                <span />
                            )}
                            <span>{description.length}/{DESCRIPTION_MAX}</span>
                        </div>
                    </div>

                    {submitError && (
                        <div className="feature-idea-field-error" role="alert" style={{ marginBottom: "0.75rem" }}>
                            {submitError}
                        </div>
                    )}

                    <div className="feature-idea-modal-actions">
                        <button
                            type="submit"
                            className={`feature-idea-modal-submit${isValid ? " is-valid" : ""}`}
                            disabled={submitting}
                            data-testid="feature-idea-submit-button"
                        >
                            {submitting ? "Posting…" : "Post to community"}
                        </button>
                    </div>
                </form>
            </div>
        </div>,
        document.body,
    );
}
